import { useEffect, useMemo, useRef, useState } from 'react';
import { COLORS } from '../../constants/theme';
import { Spinner } from '../Spinner';
import { ErrorMessage } from '../ErrorMessage';
import { formatAge } from '../../utils/formatAge';
import { sortTargetGroups, TARGET_SORT_OPTIONS } from '../../utils/intelSort';
import type { TargetSortValue } from '../../utils/intelSort';
import { IntelTargetCard } from './IntelTargetCard';
import { MobileIntelDetailScreen } from './MobileIntelDetailScreen';
import type { IntelHubShellProps } from './IntelHubTab';

/**
 * SF-157 Component 3: new mobile-only content for `IntelHubTab.tsx`'s platform switch. Owns a
 * local `selectedTargetId` — `null` shows the target-grouped list, set shows
 * `MobileIntelDetailScreen` in its place (component state, not a route change, SF-171 precedent).
 */
export function MobileIntelHubShell(props: IntelHubShellProps) {
  const {
    worldId, provinceId, targetGroups, crystalEyeReports, sortKey, onSortKey, scope, onScope,
    loading, error, totalCount, hasMore, handleShare, handleDelete, onLoadMore, onRefresh, refetchTarget,
  } = props;

  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const cardRefs = useRef(new Map<string, HTMLElement>());
  // Set on Back, consumed by the effect below once the list has re-rendered — this is what lets
  // focus land on the freshly re-mounted card DOM node rather than a stale/detached one.
  const pendingFocusRestoreRef = useRef<string | null>(null);
  // Set by the dangling-target guard below. Deliberately not just an inline `h1Ref.current?.focus()`
  // in that effect — it fires while the detail screen (not the list) is still the current render,
  // so `h1Ref` isn't attached to any DOM node yet. Consumed once the list has actually re-rendered.
  const pendingDanglingResetRef = useRef(false);

  useEffect(() => {
    h1Ref.current?.focus();
  }, []);

  // Dangling-target guard: MobileIntelHubShell — the owner of selectedTargetId — resets it to null
  // whenever it's non-null and no longer matches any targetGroups entry (deleting a target's last
  // report, or refetchTarget resolving to zero reports for it). This transition also focuses this
  // shell's own <h1> — the same fallback-to-heading rule Back uses when the originating card is
  // gone, rather than leaving focus on <body> (ux-designer C5).
  useEffect(() => {
    if (selectedTargetId != null && !targetGroups.some((g) => g.targetProvinceId === selectedTargetId)) {
      pendingFocusRestoreRef.current = null;
      pendingDanglingResetRef.current = true;
      setSelectedTargetId(null);
    }
  }, [selectedTargetId, targetGroups]);

  // Focus restoration once the list has re-rendered (selectedTargetId is null again) — the
  // dangling-target guard's own fallback-to-<h1> takes priority; otherwise Back's originating
  // card ref if it's still present, else this shell's own <h1>.
  useEffect(() => {
    if (selectedTargetId != null) return;
    if (pendingDanglingResetRef.current) {
      pendingDanglingResetRef.current = false;
      h1Ref.current?.focus();
      return;
    }
    if (pendingFocusRestoreRef.current == null) return;
    const targetId = pendingFocusRestoreRef.current;
    pendingFocusRestoreRef.current = null;
    const cardEl = cardRefs.current.get(targetId);
    if (cardEl) {
      cardEl.focus();
    } else {
      h1Ref.current?.focus();
    }
  }, [selectedTargetId, targetGroups]);

  function handleSelect(targetId: string) {
    setSelectedTargetId(targetId);
  }

  function handleBack() {
    pendingFocusRestoreRef.current = selectedTargetId;
    setSelectedTargetId(null);
  }

  // Hoisted above the early return below (Rules of Hooks) — harmless to compute even on the
  // detail-screen branch, where it's simply unused.
  const sortedGroups = useMemo(() => sortTargetGroups(targetGroups, sortKey), [targetGroups, sortKey]);

  if (selectedTargetId != null) {
    return (
      <MobileIntelDetailScreen
        worldId={worldId}
        provinceId={provinceId}
        targetProvinceId={selectedTargetId}
        targetGroups={targetGroups}
        loading={loading}
        error={error}
        onBack={handleBack}
        handleShare={handleShare}
        handleDelete={handleDelete}
        refetchTarget={refetchTarget}
      />
    );
  }

  const isBlockingLoad = loading && targetGroups.length === 0 && crystalEyeReports.length === 0;
  const isBlockingError = Boolean(error) && targetGroups.length === 0 && crystalEyeReports.length === 0;

  return (
    <div style={{ padding: 16 }}>
      <h1
        ref={h1Ref}
        tabIndex={-1}
        style={{ margin: '0 0 10px', color: COLORS.text.primary, fontSize: 18, fontWeight: 700, outline: 'none' }}
      >
        Intel Hub
      </h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <button
          type="button"
          onClick={() => onScope('mine')}
          style={{
            flex: 1, minHeight: 44,
            backgroundColor: scope === 'mine' ? COLORS.link : 'transparent',
            border: `1px solid ${scope === 'mine' ? COLORS.link : COLORS.border}`,
            borderRadius: 4,
            color: scope === 'mine' ? '#fff' : COLORS.text.secondary,
            fontSize: 12, cursor: 'pointer',
          }}
        >
          My Intel
        </button>
        <button
          type="button"
          onClick={() => onScope('kingdom')}
          style={{
            flex: 1, minHeight: 44,
            backgroundColor: scope === 'kingdom' ? COLORS.link : 'transparent',
            border: `1px solid ${scope === 'kingdom' ? COLORS.link : COLORS.border}`,
            borderRadius: 4,
            color: scope === 'kingdom' ? '#fff' : COLORS.text.secondary,
            fontSize: 12, cursor: 'pointer',
          }}
        >
          Kingdom Intel
        </button>
        {!loading && (
          <button
            type="button"
            onClick={onRefresh}
            style={{
              minHeight: 44, minWidth: 44,
              backgroundColor: 'transparent',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 4,
              color: COLORS.text.muted,
              fontSize: 12, cursor: 'pointer', padding: '0 10px',
            }}
          >
            Refresh
          </button>
        )}
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={{ display: 'block', fontSize: 11, color: COLORS.text.tertiary, marginBottom: 4 }}>
          Sort
          <select
            aria-label="Sort scouted targets by"
            value={sortKey}
            onChange={(e) => onSortKey(e.target.value as TargetSortValue)}
            style={{
              display: 'block', width: '100%', minHeight: 44, marginTop: 4,
              background: COLORS.cardAlt, border: `1px solid ${COLORS.border}`,
              borderRadius: 4, color: COLORS.text.primary, fontSize: 13, padding: '6px 8px',
            }}
          >
            {TARGET_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
      </div>

      {error && !isBlockingError && <ErrorMessage message={error} />}

      <div aria-live="polite" style={{ color: COLORS.text.tertiary, fontSize: 12, marginBottom: 8 }}>
        {targetGroups.length} scouted target{targetGroups.length !== 1 ? 's' : ''}
      </div>

      {isBlockingLoad && <Spinner />}
      {isBlockingError && <ErrorMessage message={error!} />}

      {!isBlockingLoad && !isBlockingError && (
        <>
          {sortedGroups.map((group) => (
            <div
              key={group.targetProvinceId}
              ref={(el) => {
                // Store the card's own focusable <button> root, not this plain wrapper — it's
                // what actually receives focus on Back (item 10).
                const button = el?.querySelector<HTMLElement>('button');
                if (button) cardRefs.current.set(group.targetProvinceId, button);
                else cardRefs.current.delete(group.targetProvinceId);
              }}
            >
              <IntelTargetCard group={group} onSelect={handleSelect} />
            </div>
          ))}

          {hasMore && (
            <button
              type="button"
              onClick={onLoadMore}
              style={{
                width: '100%', minHeight: 44, marginTop: 8,
                background: COLORS.cardAlt, border: `1px solid ${COLORS.border}`,
                borderRadius: 6, color: COLORS.text.secondary, fontSize: 13, cursor: 'pointer',
              }}
            >
              Load more
            </button>
          )}

          {crystalEyeReports.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ color: COLORS.text.tertiary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Kingdom Events
              </div>
              {crystalEyeReports.map((r) => {
                const data = r.data as { kingdomName?: string; location?: string; events?: { tick: number; summary: string }[] };
                return (
                  <div key={r.id} style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text.primary }}>
                      {data.kingdomName ?? r.targetKingdomName} ({data.location ?? r.targetKingdomLocation}) — {formatAge(r.capturedAt)}
                    </div>
                    {(data.events ?? []).map((e, i) => (
                      <div key={i} style={{ fontSize: 11, color: COLORS.text.secondary, marginTop: 2 }}>
                        {e.summary}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <div style={{ color: COLORS.text.muted, fontSize: 11, marginTop: 12 }}>
        {totalCount} total report{totalCount !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
